/**
 * Carousel to render items according to our design. The existing carousel, which is based on an external library,
 * has limitations and is unable to satisfy web revamp. The lib that our existing carousel is based on is out of date
 * with no active development or maintenance
 *
 * reference - https://dev.to/min11benja/how-to-make-a-horizontal-moving-carousel-with-vanilla-js-ts-elc
 * https://www.freecodecamp.org/news/how-to-build-an-image-carousel-component/
 * https://raddy.dev/blog/netflix-carousel-using-css/
 * https://medium.com/@andrew.tham.cc/recreating-netflixs-slider-component-2d6ad9009ab0
 *
 * this will be our own carousel and will be added to UI library
 */

import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  useMemo
} from 'react';
import { CarouselHandleProps, CarouselProps } from './Carousel.types';

import './carousel.styles.scss';
import { CardTracker } from './CardTracker';
import { CHEVRON_LEFT_ICON, CHEVRON_RIGHT_ICON } from '@Images';

export const Carousel = forwardRef<CarouselHandleProps, CarouselProps>(
  (
    {
      items,
      renderItem,
      className,
      cardWidth = 335,
      cardGap = 16,
      showIndicators,
      showDots,
      transitionTime,
      loop = true
    },
    ref
  ) => {
    const trackerRef = useRef<HTMLDivElement>(null);
    const activeIndexRef = useRef(0);

    const [activeIndex, setActiveIndex] = useState(0);
    const [measuredWidth, setMeasuredWidth] = useState<number>(cardWidth);
    const [reducedMotion, setReducedMotion] = useState(false);

    const [isPaused, setIsPaused] = useState(false);

    const itemsLength = items.length;

    const isEmpty = itemsLength === 0;
    const step = (cardWidth ?? measuredWidth) + cardGap;
    // transitionTime being set IS what turns autoplay on — no separate flag.
    const isAutoPlaying =
      !!transitionTime && !isEmpty && itemsLength > 1 && !reducedMotion && !isPaused;

    const scrollToIndex = useCallback(
      (i: number) => {
        if (isEmpty) return;
        const clamped = Math.max(0, Math.min(i, itemsLength - 1));
        trackerRef.current?.scrollTo({
          left: clamped * step,
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
        setActiveIndex(clamped);
      },
      [isEmpty, itemsLength, step, reducedMotion]
    );

    useImperativeHandle(
      ref,
      () => ({
        goTo: scrollToIndex,
        next: () => scrollToIndex(activeIndexRef.current + 1),
        prev: () => scrollToIndex(activeIndexRef.current - 1),
        getActiveIndex: () => activeIndexRef.current,
        pause: () => setIsPaused(true),
        play: () => setIsPaused(false),
        isPlaying: () => isAutoPlaying
      }),
      [scrollToIndex, isAutoPlaying]
    );

    useEffect(() => {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mq.matches);
      const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
      mq.addEventListener?.('change', handler);
      return () => mq.removeEventListener?.('change', handler);
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      if (cardWidth != null) return; // explicit prop wins, skip measuring
      const track = trackerRef.current;
      if (!track || typeof ResizeObserver === 'undefined') return;

      const firstCard = track.firstElementChild as HTMLElement | null;
      if (!firstCard) return;

      const ro = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width && width > 0) setMeasuredWidth(width);
      });
      ro.observe(firstCard);
      return () => ro.disconnect();
    }, [cardWidth, isEmpty]);

    useEffect(() => {
      setActiveIndex((prev) => Math.max(0, Math.min(prev, itemsLength - 1)));
    }, [itemsLength]);

    useEffect(() => {
      activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    useEffect(() => {
      const track = trackerRef.current;
      if (!track || isEmpty) return;

      let raf: number | null = null;
      let lastSeen = activeIndex;

      const onScroll = () => {
        if (raf !== null) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          if (step <= 0) return;
          const i = Math.round(track.scrollLeft / step);
          const clamped = Math.max(0, Math.min(i, itemsLength - 1));
          if (clamped !== lastSeen) {
            lastSeen = clamped;
            setActiveIndex(clamped);
          }
        });
      };

      track.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        track.removeEventListener('scroll', onScroll);
        if (raf !== null) cancelAnimationFrame(raf);
      };
      // eslint-disable-next-line
    }, [isEmpty, itemsLength, step]);

    useEffect(() => {
      if (typeof document === 'undefined') return;
      const onVisibility = () => {
        if (document.hidden) setIsPaused(true);
      };
      document.addEventListener('visibilitychange', onVisibility);
      return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    useEffect(() => {
      if (!isAutoPlaying || !transitionTime) return;

      const timer = setTimeout(() => {
        const next = activeIndex + 1;
        if (next > itemsLength - 1) {
          if (loop) scrollToIndex(0);
          // if not looping, autoplay simply stops advancing at the last card
        } else {
          scrollToIndex(next);
        }
      }, transitionTime);

      return () => clearTimeout(timer);
    }, [isAutoPlaying, activeIndex, itemsLength, loop, transitionTime, scrollToIndex]);

    const dots = useMemo(
      () => items.map((item, i) => item.name ?? `${item.heading}-${i}`),
      [items]
    );

    if (isEmpty) {
      return null;
    }

    return (
      <div
        className={`nium-carousel-wrapper ${className ?? ''}`}
        onMouseEnter={() => transitionTime && setIsPaused(true)}
        onMouseLeave={() => transitionTime && setIsPaused(false)}
        onFocus={() => transitionTime && setIsPaused(true)}
        onBlur={(e) => {
          // only resume if focus left the whole carousel, not just moved
          // between two elements inside it
          if (transitionTime && !e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsPaused(false);
          }
        }}
      >
        <div
          className="nium-carousel-viewport"
          role="region"
          aria-roledescription="carousel"
          aria-label="carousel"
        >
          <CardTracker
            items={items}
            trackRef={trackerRef}
            renderItem={renderItem}
            onCardFocus={setActiveIndex}
          />

          {showIndicators ? (
            <>
              <button
                className="nium-carousel-nav-button nium-carousel-nav-button-left"
                onClick={() => scrollToIndex(activeIndex - 1)}
                aria-label="Previous slide"
                disabled={activeIndex === 0}
                type="button"
              >
                <img src={CHEVRON_LEFT_ICON} alt="next" height={18} width={18} aria-hidden="true" />
              </button>
              <button
                className="nium-carousel-nav-button nium-carousel-nav-button-right"
                onClick={() => scrollToIndex(activeIndex + 1)}
                aria-label="Next slide"
                disabled={activeIndex === itemsLength - 1}
                type="button"
              >
                <img
                  src={CHEVRON_RIGHT_ICON}
                  alt="next"
                  height={18}
                  width={18}
                  aria-hidden="true"
                />
              </button>{' '}
            </>
          ) : null}

          {transitionTime ? (
            <button
              className="nium-carousel-play-pause"
              onClick={() => setIsPaused((prev) => !prev)}
              aria-label={isPaused ? 'Play carousel' : 'Pause carousel'}
              aria-pressed={isPaused}
              type="button"
            >
              {isPaused ? '▶' : '❚❚'}
            </button>
          ) : null}
        </div>

        {itemsLength > 1 && showDots ? (
          <div className="nium-carousel-dots" role="tablist" aria-label="Slide navigation">
            {dots.map((id, i) => (
              <button
                key={id}
                className={`nium-carousel-dot ${
                  i === activeIndex ? 'nium-carousel-dot-active' : ''
                }`}
                onClick={() => scrollToIndex(i)}
                role="tab"
                aria-selected={i === activeIndex}
                aria-label={`Go to slide ${i + 1} of ${itemsLength}`}
                type="button"
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }
);
